// The OS share sheet (Web Share API) for phones and tablets.
//
// Why: opening `https://wa.me/?text=` from a browser hands the link to the WhatsApp
// app, and on iOS the app can open on its chat list without using the text (seen
// when WhatsApp is already running). The share sheet always shows the OS picker and
// lists WhatsApp, Messages, Instagram, Telegram, ... so there is no per-app link to
// get wrong. Desktop keeps the app's own modal: desktop browsers either lack the API
// or show a sheet people don't expect.
import { isMobile, isIPad13 } from 'react-device-detect';

export type NativeShareResult =
  /** The visitor picked a target and the share went through. */
  | 'shared'
  /** The visitor closed the sheet. Not a failure: do nothing. */
  | 'cancelled'
  /** Not a phone/tablet, no Web Share API, or the browser refused (e.g. an iframe
   *  without the `web-share` permission): fall back to the app's own share modal. */
  | 'unavailable';

/** True on phones / tablets whose browser exposes the Web Share API. */
export const canUseNativeShare = (): boolean =>
  (isMobile || isIPad13) && typeof navigator !== 'undefined' && typeof navigator.share === 'function';

/**
 * Opens the OS share sheet. Call it straight from the tap handler: the browser only
 * allows `navigator.share` while the tap's user activation is still live, so nothing
 * may be awaited before this runs.
 */
export async function shareNatively(data: ShareData): Promise<NativeShareResult> {
  if (!canUseNativeShare()) return 'unavailable';
  if (typeof navigator.canShare === 'function' && !navigator.canShare(data)) return 'unavailable';
  try {
    await navigator.share(data);
    return 'shared';
  } catch (error) {
    return (error as DOMException)?.name === 'AbortError' ? 'cancelled' : 'unavailable';
  }
}
